import React, { useState } from 'react';
import { AlertTriangleIcon } from 'lucide-react';
import { ALLERGENS, CATERING_CATEGORIES, CATERING_UNITS, DIETS, OCCASIONS, Allergen, CateringCategory, CateringItem, CateringUnit, Diet, Occasion, Place } from '../../data/smaken';
import { CateringCard } from '../CateringCard';
import { ImageInput } from './ImageInput';
import { Chip, Field, FieldLabel, GhostButton, Modal, PrimaryButton, SelectField, TextArea } from './ui';
interface ProductEditorProps {
  place: Place;
  sections: string[];
  initial: CateringItem;
  isNew: boolean;
  onSave: (item: CateringItem) => void;
  onClose: () => void;
}
function toggle<T>(list: T[] | undefined, value: T): T[] {
  const current = list ?? [];
  return current.includes(value) ? current.filter(v => v !== value) : [...current, value];
}
export function ProductEditor({
  place,
  sections,
  initial,
  isNew,
  onSave,
  onClose
}: ProductEditorProps) {
  const [draft, setDraft] = useState<CateringItem>(initial);
  const [newSection, setNewSection] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const set = <K extends keyof CateringItem,>(key: K, value: CateringItem[K]) => setDraft(d => ({
    ...d,
    [key]: value
  }));
  const sectionOptions = [...new Set([...sections, draft.section].filter(Boolean))];
  const save = () => {
    const problems: string[] = [];
    const section = newSection.trim() || draft.section;
    if (!draft.name.trim()) problems.push('Produkten behöver ett namn.');
    if (!section) problems.push('Välj eller skapa en menydel.');
    if (!draft.onRequest && !(draft.price > 0)) problems.push('Ange pris, eller markera "pris vid förfrågan".');
    if (draft.occasions.length === 0) problems.push('Välj minst ett tillfälle.');
    setErrors(problems);
    if (problems.length) return;
    onSave({
      ...draft,
      name: draft.name.trim(),
      section
    });
  };
  const allergensDeclared = draft.allergens !== undefined;
  return <Modal wide title={isNew ? 'Ny produkt' : `Redigera ${initial.name}`} onClose={onClose} footer={<>
          <GhostButton onClick={onClose} data-id="element-264" data-mp="12jxju1izd6ls">Avbryt</GhostButton>
          <PrimaryButton onClick={save} data-id="element-265" data-mp="12lidzq1dunj4">{isNew ? 'Lägg till produkt' : 'Spara ändringar'}</PrimaryButton>
        </>} data-id="element-263" data-mp="1x87jcz18jzuj">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1.5fr),minmax(0,1fr)]" data-id="element-266" data-mp="1cgsl2s6n1l3r">
        <div className="space-y-8" data-id="element-267" data-mp="orr8qtuoppt7">
          {errors.length > 0 && <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700" data-id="element-268" data-mp="opn4ubwscqsx">
              {errors.map(e => <p key={e} data-id="element-269" data-mp="jqts4lpbp3tt">{e}</p>)}
            </div>}

          {/* Basics */}
          <section className="space-y-5" data-id="element-270" data-mp="tqubu8r3tlyj">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-271" data-mp="tpsyfxmm8ndn">Grunduppgifter</h3>
            <ImageInput label="Produktbild" value={draft.image} onChange={v => set('image', v)} focus={draft.imageFocus} onFocusChange={v => set('imageFocus', v)} data-id="element-272" data-mp="60ghucoyld1b" />
            <Field label="Namn" value={draft.name} onChange={v => set('name', v)} placeholder="Fig & Pig" data-id="element-273" data-mp="2cnfh9qp4oyz7" />
            <TextArea label="Beskrivning" rows={3} value={draft.description} onChange={v => set('description', v)} placeholder="Kort och säljande — det som syns på kortet." data-id="element-274" data-mp="2hi1wk4ybzbok" />
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-275" data-mp="2vhb9mvr8ppob">
              <div data-id="element-276" data-mp="30eup1yzoc8hu">
                <SelectField label="Menydel" value={newSection ? '__new' : draft.section} onChange={v => v === '__new' ? setNewSection(' ') : (setNewSection(''), set('section', v))} options={[...sectionOptions.map(s => ({
                value: s,
                label: s
              })), {
                value: '__new',
                label: '+ Ny menydel…'
              }]} data-id="element-277" data-mp="30fcv1nc5212y" />
                {newSection && <input autoFocus value={newSection.trimStart()} onChange={e => setNewSection(e.target.value || ' ')} placeholder="t.ex. Smörgåstårtor" className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-black" data-id="element-278" data-mp="1v8mqpp3flmbs" />}
              </div>
              <SelectField label="Typ" value={draft.category} onChange={v => set('category', v as CateringCategory)} options={CATERING_CATEGORIES.map(c => ({
              value: c,
              label: c
            }))} data-id="element-279" data-mp="2e80lg98zj13c" />
            </div>
            <div data-id="element-280" data-mp="2089ayihog2u3">
              <FieldLabel data-id="element-281" data-mp="208r599ks46zd">Tillfällen</FieldLabel>
              <div className="flex flex-wrap gap-2" data-id="element-282" data-mp="20a28n8abbcpy">
                {OCCASIONS.map(o => <Chip key={o} active={draft.occasions.includes(o)} onClick={() => set('occasions', toggle<Occasion>(draft.occasions, o))} data-id="element-283" data-mp="ssbahimx2xs0">
                    {o}
                  </Chip>)}
              </div>
            </div>
          </section>

          {/* Contents */}
          <section className="space-y-5 border-t border-gray-100 pt-8" data-id="element-284" data-mp="34i0zwrug440z">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-285" data-mp="39ft1kdkw74id">Innehåll & allergener</h3>
            <TextArea label="Ingredienser" rows={3} value={draft.ingredients ?? ''} onChange={v => set('ingredients', v)} placeholder="Surdegsbröd (vetemjöl, vatten, salt), taleggio, prosciutto…" hint="Skriv som på förpackningen. Visas när kunden öppnar produkten." data-id="element-286" data-mp="3e8m9197qwqy5" />
            <div data-id="element-287" data-mp="112ae288jnf4">
              <div className="mb-2 flex items-center justify-between gap-3" data-id="element-288" data-mp="11kh5ssixk1a">
                <FieldLabel data-id="element-289" data-mp="1xtcc7cds9z5u">Allergener</FieldLabel>
                <label className="mb-2 flex cursor-pointer items-center gap-2 text-xs text-gray-600" data-id="element-290" data-mp="1xum8px4zmzad">
                  <input type="checkbox" checked={allergensDeclared && draft.allergens!.length === 0} onChange={e => set('allergens', e.target.checked ? [] : undefined)} className="h-4 w-4 accent-black" data-id="element-291" data-mp="22sxkweir9lvr" />
                  Fri från alla 14
                </label>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-292" data-mp="3a3va7x7r9wy1">
                {ALLERGENS.map(a => <Chip key={a} tone="warn" active={draft.allergens?.includes(a) ?? false} onClick={() => set('allergens', toggle<Allergen>(draft.allergens, a))} data-id="element-293" data-mp="3p15kx4ckqysp">
                    {a}
                  </Chip>)}
              </div>
              {!allergensDeclared && <p className="mt-3 flex items-center gap-2 text-xs text-amber-700" data-id="element-294" data-mp="bmhnjt3u68u6">
                  <AlertTriangleIcon className="h-3.5 w-3.5" data-id="element-295" data-mp="6og1czb45teh" />
                  Inte deklarerat ännu — kunden ser "Allergener kompletteras".
                </p>}
            </div>
            <div data-id="element-296" data-mp="v8sybyucvs6q">
              <FieldLabel data-id="element-297" data-mp="v9cfveg3gek8">Specialkost</FieldLabel>
              <div className="flex flex-wrap gap-2" data-id="element-298" data-mp="v70097xt6gac">
                {DIETS.map(d => <Chip key={d} active={draft.diets?.includes(d) ?? false} onClick={() => set('diets', toggle<Diet>(draft.diets, d))} data-id="element-299" data-mp="gbk50zlhsecd">
                    {d}
                  </Chip>)}
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section className="space-y-5 border-t border-gray-100 pt-8" data-id="element-300" data-mp="cbrq855i9byk">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-301" data-mp="3trhqq8ba6y4c">Pris & villkor</h3>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700" data-id="element-302" data-mp="2k5gkd8tfkxx">
              <input type="checkbox" checked={draft.onRequest ?? false} onChange={e => set('onRequest', e.target.checked || undefined)} className="h-4 w-4 accent-black" data-id="element-303" data-mp="3kqa5orxrzts7" />
              Pris vid förfrågan
            </label>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-304" data-mp="42x5wt7wnwiz">
              {!draft.onRequest && <Field label="Pris inkl. moms" type="number" value={draft.price ? String(draft.price) : ''} onChange={v => set('price', Number(v))} placeholder="145" data-id="element-305" data-mp="3v85jysiprezo" />}
              <SelectField label="Enhet" value={draft.unit} onChange={v => set('unit', v as CateringUnit)} options={CATERING_UNITS.map(u => ({
              value: u,
              label: u
            }))} data-id="element-306" data-mp="3hh0gffc862k2" />
              <Field label="Minsta antal" type="number" value={String(draft.minGuests)} onChange={v => set('minGuests', Math.max(1, Number(v) || 1))} data-id="element-307" data-mp="37l751za8sf3v" />
              <Field label="Dagars varsel" type="number" value={String(draft.leadTime)} onChange={v => set('leadTime', Math.max(0, Number(v) || 0))} data-id="element-308" data-mp="1e0ne3okv59sh" />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700" data-id="element-309" data-mp="1n2mvwj3pchwh">
              <input type="checkbox" checked={draft.hidden ?? false} onChange={e => set('hidden', e.target.checked || undefined)} className="h-4 w-4 accent-black" data-id="element-310" data-mp="1rz3kfeetcya3" />
              Dold på sajten (utkast)
            </label>
          </section>
        </div>

        {/* Live preview — exactly the public card */}
        <aside className="lg:sticky lg:top-6 lg:self-start" data-id="element-311" data-mp="192lvf4rcnchj">
          <FieldLabel data-id="element-312" data-mp="bm6bebdajb2r">Så här ser kunden den</FieldLabel>
          <div className="pointer-events-none rounded-2xl bg-papper p-5" aria-hidden="true" data-id="element-313" data-mp="bln55rx06b4k">
            <CateringCard showPlace offer={{
            ...draft,
            name: draft.name || 'Produktnamn',
            description: draft.description || 'Beskrivningen syns här.',
            section: newSection.trim() || draft.section,
            placeSlug: place.slug,
            placeName: place.name,
            placeCity: String(place.city)
          }} data-id="element-314" data-mp="gjydtzbkafl7" />
          </div>
        </aside>
      </div>
    </Modal>;
}